<template>
    <div class="space-y-3">
        <LogicSubjectSection :page="page" :graph="graph" :node="node" :context="context" />

        <form-select name="Property" v-model="draft.property">
            <option v-if="draft.property && !properties.includes(draft.property)" :value="draft.property">{{ draft.property }} · saved</option>
            <option v-for="property in properties" :key="property" :value="property">{{ property }}</option>
        </form-select>

        <section class="space-y-2">
            <form-label>Cases (the first that matches runs)</form-label>
            <div v-for="(item, index) in draft.cases" :key="item.id" class="space-y-0.5">
                <div class="flex items-center gap-1.5">
                    <form-select class="w-16 flex-shrink-0 text-sm" v-model="item.operator">
                        <option v-for="op in CONDITION_OPERATORS" :key="op" :value="op">{{ op }}</option>
                    </form-select>
                    <form-input v-model="item.value" type="text" placeholder="value" class="flex-1 text-sm" />
                    <button v-if="index > 0" class="text-xs text-gray-300 hover:text-white" title="Check this case earlier"
                        @click="moveUp(index)">↑</button>
                    <button class="ha-builder-danger-button" title="Remove case and its wires" @click="draft.cases.splice(index, 1)">×</button>
                </div>
                <LogicRunList :page="page" :graph="graph" :node="node" :port="switchCasePort(item.id)"
                    :label="`Case ${describeCase(item)}`" empty="Drag a wire from this case to an action." />
            </div>
            <button class="ha-builder-link-button" @click="addCase">+ case</button>
            <LogicRunList :page="page" :graph="graph" :node="node" :port="SWITCH_ELSE_PORT" label="Else"
                empty="Drag a wire from Else to what runs when no case matches." />
        </section>

        <form-checkbox label="Only when the case changes" v-model="draft.onChange" />
        <p class="text-xs text-gray-400">
            Runs a case once when the value moves into it, e.g. when a sensor moves into another range.
        </p>
    </div>
</template>

<script setup>
import { computed, toRaw } from 'vue';
import { useLogicStore } from '../../stores/logicStore.js';
import { CONDITION_OPERATORS, SWITCH_ELSE_PORT, switchCasePort } from '../../Hasp/properties/v2/logic/index.js';
import { describeSwitchCase as describeCase } from '../../Hasp/properties/v2/graphPorts.js';
import { getSubjectPropertyChoices } from '../../logic/logicFlow.js';
import { useNodeDraft } from '../hass/useNodeDraft.js';
import LogicRunList from './LogicRunList.vue';
import LogicSubjectSection from './LogicSubjectSection.vue';

/** A switch node: what it compares, its cases in order with what each runs, then Else. */
const props = defineProps({
    page: { type: Object, required: true },
    graph: { type: Object, required: true },
    node: { type: Object, required: true },
    context: { type: Object, required: true },
});
const logic = useLogicStore();

const properties = computed(() => getSubjectPropertyChoices(props.graph, props.node.id, props.context, props.page));

const pick = data => ({ property: data.property ?? '', cases: data.cases ?? [], onChange: Boolean(data.onChange) });
const draft = useNodeDraft({
    build: () => structuredClone(pick(toRaw(props.node).data)),
    snapshot: value => value,
    current: () => pick(props.node.data),
    commit: data => logic.setNodeData(props.page, props.node.id, { ...toRaw(props.node).data, ...data }),
});

function addCase() {
    const ids = draft.value.cases.map(item => Number(String(item.id).replace(/^c/, '')) || 0);
    draft.value.cases.push({ id: `c${Math.max(0, ...ids) + 1}`, operator: '==', value: '' });
}

function moveUp(index) {
    const cases = draft.value.cases;
    cases.splice(index - 1, 2, cases[index], cases[index - 1]);
}
</script>
